import { z } from "zod";
import type { ModuleKey } from "./modules.js";

/**
 * Aan de slag (issue #162): een nieuwe organisatie krijgt een welkomstscherm, een voorbeeldgebouw en een
 * checklist die zichzelf afvinkt op basis van echte gegevens. Alleen voor organisaties die via
 * Platformbeheer zijn aangemaakt (`organizations.onboarding` niet null); bestaande organisaties zien niets.
 */
export interface OnboardingState {
  /** Welkomstscherm afgerond of overgeslagen. */
  welcomeDoneAt?: string;
  /** Checklist verborgen door de gebruiker (of vanzelf: alles af). */
  checklistHiddenAt?: string;
}

export const ONBOARDING_STEPS = ["building", "plan", "issue", "assets", "invite", "qr"] as const;
export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number];

export interface OnboardingStepDef {
  title: string;
  description: string;
  /** Stap alleen tonen als de module aan staat. */
  module?: ModuleKey;
}

export const ONBOARDING_STEP_DEFS: Record<OnboardingStepKey, OnboardingStepDef> = {
  building: {
    title: "Voeg je eerste gebouw toe",
    description: "Naam en adres zijn genoeg om te beginnen.",
  },
  plan: {
    title: "Upload een plattegrond",
    description: "Een PDF per verdieping. Daarop zet je bevindingen en assets.",
  },
  issue: {
    title: "Zet een bevinding op de plattegrond",
    description: "Tik op de tekening, beantwoord de vragen en maak een foto.",
    module: "issues",
  },
  assets: {
    title: "Importeer je assets",
    description: "Vanuit Excel of een Assetlog-export, of voeg ze één voor één toe.",
    module: "assets",
  },
  invite: {
    title: "Nodig een collega uit",
    description: "Kies per collega een rol en welke gebouwen die ziet.",
  },
  qr: {
    title: "Zet meldingen via QR-code aan",
    description: "Gebruikers van het gebouw melden een probleem zonder in te loggen.",
    module: "intake",
  },
};

export interface OnboardingStep {
  key: OnboardingStepKey;
  done: boolean;
  /** Waar de gebruiker de stap uitvoert (route in de app). */
  href: string;
}

export interface OnboardingDto {
  /** Welkomstscherm tonen (eerste keer, eigenaar of beheerder). */
  showWelcome: boolean;
  /** Checklist tonen op Gebouwen. */
  showChecklist: boolean;
  steps: OnboardingStep[];
  /** Voorbeeldgebouw, als dat er (nog) is. */
  demoBuildingId: string | null;
}

export const onboardingUpdateSchema = z.object({
  welcomeDone: z.literal(true).optional(),
  checklistHidden: z.boolean().optional(),
});
export type OnboardingUpdate = z.infer<typeof onboardingUpdateSchema>;
